import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Search } from 'lucide-react';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlossaryPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/glossary`);
        if (!cancelled) setData(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  const categories = (data?.categories || []).map((cat) => ({
    ...cat,
    terms: cat.terms.filter((t) =>
      filter ? t.term.toLowerCase().includes(filter.toLowerCase()) : true
    ),
  })).filter((cat) => cat.terms.length > 0);

  const totalShown = categories.reduce((sum, c) => sum + c.terms.length, 0);

  return (
    <div data-testid="glossary-page" className="bg-[#FAF9F6]">
      <SEO
        title="The Jewelry Glossary — 64 Essential Terms Decoded · Jewelry Refined"
        description="The pre-owned luxury jewelry glossary — 64 terms covering cuts, settings, materials, gemstones, eras, and grading. From cabochon to plique-à-jour, every word a buyer needs."
        url="/glossary"
        type="website"
      />
      <SchemaOrg
        type="breadcrumb"
        data={{ items: [
          { name: 'Home', url: '/' },
          { name: 'Glossary', url: '/glossary' },
        ]}}
      />

      {/* Editorial Hero */}
      <section className="bg-[#111] text-white">
        <div className="container-editorial pt-20 pb-16 lg:pt-28 lg:pb-20">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Glossary</span>
          </nav>
          <p className="overline text-[#C69C6D] mb-6">The Dictionary</p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8">
            The <span className="italic">Glossary</span>
          </h1>
          <p className="font-heading italic text-xl lg:text-2xl text-white/85 max-w-3xl mb-10">
            Sixty-four terms every pre-owned luxury jewelry buyer should know — cuts, settings, eras, and certifications, decoded with editorial bite.
          </p>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" strokeWidth={1.5} />
            <input
              data-testid="glossary-search"
              type="search"
              placeholder="Search 64 terms…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-[#C69C6D] tracking-wide"
            />
          </div>
          <p className="overline text-white/50 mt-6">{totalShown} terms shown</p>
        </div>
      </section>

      {/* Categories */}
      <section className="section-editorial">
        <div className="container-editorial">
          {categories.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-10 h-10 text-[#C69C6D] mx-auto mb-6" strokeWidth={1.2} />
              <p className="font-heading text-2xl text-[#555]">No terms match — try a different word.</p>
            </div>
          ) : (
            categories.map((cat) => (
              <div key={cat.name} className="mb-16 last:mb-0" data-testid={`glossary-category-${cat.name.toLowerCase()}`}>
                <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-[#E5E0D8]">
                  <h2 className="heading-editorial text-3xl lg:text-4xl">{cat.name}</h2>
                  <p className="overline text-[#888]">{cat.terms.length} {cat.terms.length === 1 ? 'term' : 'terms'}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                  {cat.terms.map((t) => (
                    <Link
                      key={t.slug}
                      to={`/glossary/${t.slug}`}
                      data-testid={`glossary-term-${t.slug}`}
                      className="group block pb-6 border-b border-[#E5E0D8]/60 hover:border-[#111] transition-colors"
                    >
                      <p className="font-heading text-2xl text-[#111] group-hover:text-[#C69C6D] transition-colors mb-2 font-light">
                        {t.term}
                      </p>
                      <p className="text-sm text-[#555] leading-relaxed line-clamp-2">
                        {t.definition}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default GlossaryPage;
